import React, { useState, useCallback } from 'react';
import { Upload, Image, FileImage, X, Download, Eye, Trash2, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modality icons and colors
const MODALITY_CONFIG = {
  CR: { name: 'Radiographie', color: 'bg-blue-100 text-blue-700', icon: '🦴' },
  DX: { name: 'Radiographie', color: 'bg-blue-100 text-blue-700', icon: '🦴' },
  XR: { name: 'Radiographie', color: 'bg-blue-100 text-blue-700', icon: '🦴' },
  US: { name: 'Échographie', color: 'bg-purple-100 text-purple-700', icon: '📡' },
  CT: { name: 'Scanner', color: 'bg-orange-100 text-orange-700', icon: '🔬' },
  MR: { name: 'IRM', color: 'bg-green-100 text-green-700', icon: '🧲' },
  MRI: { name: 'IRM', color: 'bg-green-100 text-green-700', icon: '🧲' },
  OT: { name: 'Autre', color: 'bg-gray-100 text-gray-700', icon: '📷' }
};

const DicomUploader = ({ animalId, token, onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Check file extension
    const validExtensions = ['.dcm', '.dicom', '.dic'];
    const hasValidExt = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExt && !file.type.includes('dicom')) {
      toast.error('Format invalide. Utilisez un fichier DICOM (.dcm)');
      return;
    }
    
    // Check file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 50 Mo.');
      return;
    }
    
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/dicom/upload/${animalId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Image DICOM importée avec succès');
        if (onUploadSuccess) onUploadSuccess(data);
      } else {
        toast.error(data.detail || 'Erreur lors de l\'import');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [animalId, token]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
        dragOver 
          ? 'border-indigo-500 bg-indigo-50' 
          : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
      }`}
    >
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
          <p className="text-gray-600">Import en cours...</p>
        </div>
      ) : (
        <>
          <FileImage className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-700 font-medium mb-1">
            Glissez un fichier DICOM ici
          </p>
          <p className="text-sm text-gray-500 mb-4">
            ou cliquez pour sélectionner (max 50 Mo)
          </p>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".dcm,.dicom,.dic,application/dicom"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files[0])}
            />
            <Button variant="outline" className="pointer-events-none">
              <Upload className="w-4 h-4 mr-2" />
              Sélectionner un fichier
            </Button>
          </label>
          <p className="text-xs text-gray-400 mt-3">
            Formats supportés : Radiographie, Échographie, Scanner, IRM
          </p>
        </>
      )}
    </div>
  );
};

const DicomViewer = ({ dicomId, token, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  React.useEffect(() => {
    const loadImage = async () => {
      try {
        const response = await fetch(`${API_URL}/api/dicom/${dicomId}/view`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          setImageUrl(URL.createObjectURL(blob));
        } else {
          setError('Erreur lors du chargement de l\'image');
        }
      } catch (err) {
        setError('Erreur de connexion');
      } finally {
        setLoading(false);
      }
    };
    
    loadImage();
    
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [dicomId, token]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">Visualisation DICOM</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 bg-black flex items-center justify-center min-h-[400px]">
          {loading && (
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          )}
          {error && (
            <div className="text-center text-white">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-red-400" />
              <p>{error}</p>
            </div>
          )}
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt="DICOM" 
              className="max-w-full max-h-[60vh] object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const DicomCard = ({ image, token, onDelete, onView }) => {
  const config = MODALITY_CONFIG[image.modality] || MODALITY_CONFIG.OT;
  const [deleting, setDeleting] = useState(false);
  
  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette image DICOM ?')) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/dicom/${image.dicom_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Image supprimée');
        if (onDelete) onDelete(image.dicom_id);
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dicom/${image.dicom_id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = image.filename || `${image.dicom_id}.dcm`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Thumbnail */}
      <div 
        className="aspect-square bg-gray-900 relative cursor-pointer group"
        onClick={() => onView(image.dicom_id)}
      >
        {image.thumbnail ? (
          <img 
            src={`data:image/png;base64,${image.thumbnail}`}
            alt={image.modality_name}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileImage className="w-16 h-16 text-gray-600" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Eye className="w-8 h-8 text-white" />
        </div>
      </div>
      
      {/* Info */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.icon} {config.name}
          </span>
        </div>
        
        <p className="text-sm font-medium text-gray-900 truncate" title={image.filename}>
          {image.filename || 'Image DICOM'}
        </p>
        
        {image.study_date && (
          <p className="text-xs text-gray-500 mt-1">
            📅 {image.study_date}
          </p>
        )}
        
        {image.study_description && (
          <p className="text-xs text-gray-500 truncate" title={image.study_description}>
            {image.study_description}
          </p>
        )}
        
        <p className="text-xs text-gray-400 mt-1">
          {(image.file_size / 1024 / 1024).toFixed(2)} Mo
        </p>
        
        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onView(image.dicom_id)}
            className="flex-1"
          >
            <Eye className="w-4 h-4 mr-1" />
            Voir
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
            className="text-red-600 hover:bg-red-50"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

const DicomGallery = ({ animalId, token, showUploader = true }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingId, setViewingId] = useState(null);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dicom/animal/${animalId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setImages(data.images || []);
      }
    } catch (error) {
      console.error('Error fetching DICOM images:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (animalId && token) {
      fetchImages();
    }
  }, [animalId, token]);

  const handleUploadSuccess = (data) => {
    fetchImages();
  };

  const handleDelete = (dicomId) => {
    setImages(images.filter(img => img.dicom_id !== dicomId));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileImage className="w-5 h-5 text-indigo-600" />
            Imagerie médicale
          </h3>
          <p className="text-sm text-gray-500">
            {images.length} image{images.length > 1 ? 's' : ''} DICOM
          </p>
        </div>
      </div>

      {/* Uploader */}
      {showUploader && (
        <DicomUploader 
          animalId={animalId} 
          token={token} 
          onUploadSuccess={handleUploadSuccess}
        />
      )}

      {/* Gallery */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <DicomCard
              key={image.dicom_id}
              image={image}
              token={token}
              onDelete={handleDelete}
              onView={setViewingId}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-xl">
          <FileImage className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-600">Aucune image DICOM</p>
          <p className="text-sm text-gray-400">
            Importez des radiographies, échographies, scanners ou IRM
          </p>
        </div>
      )}

      {/* Viewer Modal */}
      {viewingId && (
        <DicomViewer
          dicomId={viewingId}
          token={token}
          onClose={() => setViewingId(null)}
        />
      )}
    </div>
  );
};

export { DicomUploader, DicomViewer, DicomCard, DicomGallery };
export default DicomGallery;
